import React from 'react';
import { Button, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  buildInvalidOptionKey,
  buildNotOnMenuKey,
  DISMISS_REPEAT_TTS,
} from '../../redux/features/unresolvedItems/unresolvedItems.constants';
import {
  selectUnresolvedItemsCount,
  selectVisibleInvalidOptionLines,
  selectVisibleNotOnMenuCards,
} from '../../redux/features/unresolvedItems/unresolvedItems.selector';
import { dismissAllUnresolvedItems } from '../../redux/features/unresolvedItems/unresolvedItems.slice';
import { selectValidCartItems } from '../../selectors/cart';
import { selectDismissRepeatPhrases } from '../../selectors/menu';
import useTTSMessage from '../../hooks/useTTSMessage.hooks';
import useReadback from '../../hooks/useReadback/useReadback.hooks';
import Colors from '../../utils/color';
import UnresolvedItemCard, {
  UnresolvedItemVariant,
} from './UnresolvedItemCard';

const useStyles = makeStyles(({ spacing }: Theme) => ({
  container: {
    marginBottom: spacing(2),
    paddingBottom: spacing(1),
    borderBottom: `${spacing(0.125)} solid ${Colors.alto}`,
  },
  header: {
    fontWeight: 'bold !important',
    color: `${Colors.internationalOrange} !important`,
  },
  buttonsWrapper: {
    display: 'flex',
    gap: spacing(1),
    marginTop: spacing(2),
  },
  actionButton: {
    flex: 1,
    textTransform: 'none !important' as any,
    fontWeight: 'bold !important',
  },
}));

/**
 * Per-session panel surfacing the items the AI flagged as unresolved
 * (NOT_ON_MENU + invalid options) as diagnostic cards. When the agent clicks
 * "Dismiss & Repeat", a natural TTS message is sent followed by a readback
 * request so the customer hears the current order, and the panel is dismissed.
 */
export default function UnresolvedItemsPanel() {
  const { container, header, buttonsWrapper, actionButton } = useStyles();
  const dispatch = useAppDispatch();
  const { sendTTSMessage } = useTTSMessage();
  const { handleReadBack } = useReadback();

  const notOnMenuCards = useAppSelector(selectVisibleNotOnMenuCards);
  const invalidOptionLines = useAppSelector(selectVisibleInvalidOptionLines);
  const count = useAppSelector(selectUnresolvedItemsCount);
  const validCartItems = useAppSelector(selectValidCartItems);
  const hasCartItems = Object.keys(validCartItems).length > 0;
  const dismissRepeatPhrases = useAppSelector(selectDismissRepeatPhrases);

  if (count === 0) {
    return null;
  }

  const handleDismissAndRepeat = () => {
    const phrases = dismissRepeatPhrases ?? [DISMISS_REPEAT_TTS];
    const message = phrases[Math.floor(Math.random() * phrases.length)];
    sendTTSMessage(message);
    handleReadBack({ timePressed: Date.now(), forceRegularReadback: true });
    dispatch(dismissAllUnresolvedItems());
  };

  const handleDismiss = () => {
    dispatch(dismissAllUnresolvedItems());
  };

  return (
    <div id="unresolvedItems" className={container}>
      <Typography variant="subtitle2" className={header}>
        Unresolved items ({count})
      </Typography>
      {notOnMenuCards.map((card, index) => (
        <UnresolvedItemCard
          key={`not-on-menu-${buildNotOnMenuKey(card)}-${index}`}
          variant={UnresolvedItemVariant.NOT_ON_MENU}
          title={card.item}
          diagnosticLabel="couldn't resolve — not on menu"
        />
      ))}
      {invalidOptionLines.map((line, index) => (
        <UnresolvedItemCard
          key={`invalid-option-${buildInvalidOptionKey(line)}-${index}`}
          variant={UnresolvedItemVariant.INVALID_OPTION}
          title={`"${line.option}" in ${
            line.is_top_level ? 'top-level' : line.constraint ?? 'top-level'
          }`}
          diagnosticLabel="invalid option"
        />
      ))}
      <div className={buttonsWrapper}>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          className={actionButton}
          onClick={handleDismissAndRepeat}
          disabled={!hasCartItems}
        >
          Dismiss &amp; Repeat
        </Button>
        <Button
          variant="contained"
          color="warning"
          size="small"
          className={actionButton}
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}
